/* ============================================================
 * BXGY Auto-Add reconciler (theme-only)
 * ------------------------------------------------------------
 * Paired with: sections/bxgy-auto-add-rules.liquid
 *
 * What this does:
 *   - Reads the merchant-configured rules JSON emitted by the
 *     section (one or more <script type="application/json"
 *     data-bxgy-rules> tags).
 *   - Evaluates the current cart via /cart.js.
 *   - For each enabled rule, calculates the "earned" reward
 *     quantity based on qualifying spend, then adds or reduces
 *     auto-added line items to match.
 *   - Auto-added lines are tagged with private properties
 *     (prefixed with "_") so they can be identified and never
 *     mistaken for items the customer added manually.
 *
 * What this does NOT do:
 *   - It does NOT modify displayed or actual cart prices.
 *   - Real discounted pricing for the add-on item must come
 *     from a Shopify automatic discount / discount code /
 *     Shopify Function. The `discount_percent` value configured
 *     in the theme editor is stored only as a line item
 *     property (`_bxgy_discount_percent`) for alignment /
 *     reporting / future extensibility.
 *
 * Concurrency / loop safety:
 *   - A single in-flight reconcile guard (`isReconciling`).
 *   - A debounced scheduler so rapid cart events coalesce.
 *   - An internal-call counter so our own /cart/* mutations do
 *     not re-trigger a reconcile through the fetch patch.
 * ============================================================ */

(function () {
  'use strict';

  // --- small utilities --------------------------------------------------

  // TEMP: verbose logging is ON while debugging. Flip to `false` once
  // everything is working to silence the console.
  let DEBUG = true;

  const log = (...args) => {
    if (!DEBUG) return;
    try {
      console.log('[BXGY]', ...args);
    } catch (e) { /* noop */ }
  };
  // "info"-level always-on logger so you can see lifecycle events even
  // with DEBUG off. Cheap and clearly prefixed for easy filtering.
  const info = (...args) => {
    try {
      console.info('[BXGY]', ...args);
    } catch (e) { /* noop */ }
  };

  info('script loaded, evaluating init guard...');

  const getRoot = () => {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  };

  /**
   * Toggle a body-level class so CSS can style every auto-added
   * cart line as "loading" while a reconcile is pending or running.
   *
   * Using a BODY class (instead of per-item inline style) survives
   * the theme re-rendering the cart DOM mid-reconcile: even after
   * the theme replaces <cart-items> with fresh HTML, the newly
   * rendered `.cart-item--bxgy-auto` rows inherit the loading look
   * as long as <body> still carries the flag.
   *
   * We also flip the `disabled` attr on every checkout button
   * (`button[name="checkout"]`, used in both cart-drawer.liquid
   * and main-cart.liquid) so the customer can't race the
   * reconciler and check out before a pending gift line is added
   * or removed. The body-class CSS covers the visuals; this attr
   * toggle covers keyboard/Enter and programmatic form submits.
   *
   * Paired CSS lives in sections/bxgy-auto-add-rules.liquid.
   */
  const setReconcilingFlag = (on) => {
    try {
      if (typeof document === 'undefined' || !document.body) return;
      if (on) document.body.classList.add('bxgy-reconciling');
      else document.body.classList.remove('bxgy-reconciling');
      document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
        if (on) {
          btn.setAttribute('disabled', '');
          btn.setAttribute('aria-busy', 'true');
        } else {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-busy');
        }
      });
    } catch (e) { /* noop */ }
  };

  // Guard against double-init if the section is rendered more than once
  // or the script is included twice by the theme.
  if (window.__bxgyAutoAddInited) {
    info('already inited once on this page, skipping second init');
    return;
  }
  window.__bxgyAutoAddInited = true;

  // --- state -----------------------------------------------------------

  let rulesDataCache = null;
  let isReconciling = false;
  let reconcileQueued = false;
  let internalCallInFlight = 0;
  let debounceTimer = null;

  // localStorage key prefix for remembering the customer's picked
  // variant on a multi-variant reward. One entry per rule so
  // different tiers can have independent picks. The value stored
  // is the numeric variant id as a string.
  const VARIANT_PICK_STORAGE_PREFIX = 'bxgy:picked_variant:';

  // Hard cap for any single auto-added gift line.
  //
  // This is an anti-tampering measure: a customer could in theory POST
  // /cart/change.js directly (browser console, extension, DevTools) to
  // bump a free-gift line from 1 to 5. We can't prevent the request,
  // but we reconcile on every cart mutation (see patchFetch) and
  // forcibly drop any _bxgy_auto line back down to this cap.
  //
  // This is a UX/defence-in-depth measure ONLY. The authoritative
  // protection of the discount amount must live server-side in the
  // Shopify Function that applies the gift discount (it should cap the
  // quantity it is willing to make free per order/line/customer).
  const MAX_REWARD_LINE_QTY = 1;

  // --- config loading --------------------------------------------------

  /**
   * Reads and merges all `<script data-bxgy-rules>` tags emitted by the
   * section. Dedupes by `rule.id` so an accidental double render never
   * causes rules to compound on themselves.
   */
  const getRuleConfig = () => {
    if (rulesDataCache) return rulesDataCache;
    const nodes = document.querySelectorAll('script[data-bxgy-rules]');
    info('getRuleConfig: found', nodes.length, 'data-bxgy-rules <script> tag(s) in DOM');
    const combined = { enabled_global: false, rules: [] };
    const seenIds = new Set();
    for (const node of nodes) {
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        if (parsed.enabled_global) combined.enabled_global = true;
        if (Array.isArray(parsed.rules)) {
          for (const rule of parsed.rules) {
            if (!rule || !rule.id) continue;
            if (seenIds.has(rule.id)) continue;
            seenIds.add(rule.id);
            combined.rules.push(rule);
          }
        }
      } catch (e) {
        console.warn('[BXGY] Failed to parse rules JSON', e);
      }
    }
    rulesDataCache = combined;
    info('getRuleConfig: parsed config =', combined);
    return combined;
  };

  // --- cart helpers ----------------------------------------------------

  const getCart = () => fetch(`${getRoot()}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }).then((res) => {
    if (!res.ok) throw new Error(`Failed to fetch cart (${res.status})`);
    return res.json();
  });

  /**
   * An auto-added line is identified solely by its private properties.
   * Never treat a manually-added product as auto-added, even if it
   * happens to be the same variant as the reward.
   *
   * Other systems can also tag lines with `_bxgy_auto: "true"` so the
   * same Shopify discount Function applies to them (e.g. the
   * custom-bundle-set free-gift uses this contract). To avoid
   * stealing or removing those lines via orphan cleanup / quantity
   * clamp, we ignore any line whose `_bxgy_source` is set to
   * something other than the theme-auto-add reconciler. Untagged
   * lines (older auto-add lines without `_bxgy_source`) are still
   * treated as ours for backwards compatibility.
   */
  const isAutoAddedLine = (item, ruleId) => {
    if (!item || !item.properties) return false;
    const props = item.properties;
    if (String(props._bxgy_auto) !== 'true') return false;
    const source = props._bxgy_source;
    if (source != null && String(source) !== 'theme-auto-add') return false;
    if (ruleId != null && String(props._bxgy_rule_id) !== String(ruleId)) {
      return false;
    }
    return true;
  };

  /**
   * Sum of qualifying-line prices (in cents), excluding any auto-added
   * line items (from any rule) so rewards cannot bootstrap more rewards.
   *
   * `eligible_scope === 'entire_cart'`: every non–auto-added line counts
   * (no product-id filter). Otherwise uses `eligible_product_ids`.
   */
  const getEligibleSubtotal = (cart, rule) => {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const scope = rule && rule.eligible_scope === 'entire_cart'
      ? 'entire_cart'
      : 'collection_filter';
    const eligibleIds = new Set((rule.eligible_product_ids || []).map(String));
    let subtotal = 0;
    for (const item of cart.items) {
      if (isAutoAddedLine(item)) continue;
      if (scope !== 'entire_cart' && !eligibleIds.has(String(item.product_id))) continue;
      // `final_line_price` is post line-item-discounts, so thresholds
      // reflect what the customer is actually paying for the line.
      const linePrice = typeof item.final_line_price === 'number'
        ? item.final_line_price
        : item.line_price;
      subtotal += linePrice || 0;
    }
    return subtotal;
  };

  /**
   * Pick the right threshold (in cents) for the given cart currency.
   * CAD is the primary / fallback. USD has its own value when set;
   * otherwise falls back to CAD so the rule still fires.
   */
  const getThresholdCents = (rule, currencyCode) => {
    const currency = String(currencyCode || '').toUpperCase();
    const cadCents = Number(rule.min_spend_cad_cents) || 0;
    const usdCents = Number(rule.min_spend_usd_cents) || 0;
    if (currency === 'USD' && usdCents > 0) return usdCents;
    return cadCents;
  };

  /**
   * How many reward units the customer has "earned" for this rule,
   * given a qualifying subtotal in cents and the active cart currency.
   */
  const getEarnedQuantity = (rule, eligibleSubtotalCents, currencyCode) => {
    if (!rule || !rule.enabled) return 0;
    if (!rule.reward_variant_id) return 0;
    const threshold = getThresholdCents(rule, currencyCode);
    if (threshold <= 0) return 0;
    if (eligibleSubtotalCents < threshold) return 0;

    const qtyPer = Math.max(1, Number(rule.reward_quantity) || 1);
    const multiples = Math.floor(eligibleSubtotalCents / threshold);

    switch (rule.repeat_mode) {
      case 'per_multiple':
        return multiples * qtyPer;
      case 'capped_multiple': {
        const cap = Math.max(1, Number(rule.max_awards) || 1);
        return Math.min(multiples, cap) * qtyPer;
      }
      case 'once':
      default:
        return qtyPer;
    }
  };

  const getCurrentAutoAddedLines = (cart, ruleId) => {
    const result = [];
    if (!cart || !Array.isArray(cart.items)) return result;
    cart.items.forEach((item, i) => {
      if (isAutoAddedLine(item, ruleId)) {
        // line numbers in /cart/change.js are 1-indexed
        result.push({ line: i + 1, quantity: item.quantity, item });
      }
    });
    return result;
  };

  const getCurrentAutoAddedQuantity = (cart, ruleId) => {
    const lines = getCurrentAutoAddedLines(cart, ruleId);
    return lines.reduce((total, l) => total + l.quantity, 0);
  };

  // --- cart mutations --------------------------------------------------

  const internalFetch = (url, options) => {
    internalCallInFlight++;
    return fetch(url, options).then((res) => {
      internalCallInFlight = Math.max(0, internalCallInFlight - 1);
      return res;
    }, (err) => {
      internalCallInFlight = Math.max(0, internalCallInFlight - 1);
      throw err;
    });
  };

  // --- variant selection (multi-variant reward products) --------------
  //
  // Strategy: auto-add the first-available variant on qualify, then
  // let the customer swap from a small picker on the cart line. Their
  // pick is remembered across reloads via localStorage so the drawer
  // and the cart page stay in sync, and so they don't have to re-pick
  // every time they re-qualify.

  const getFirstAvailableVariantId = (rule) => {
    const variants = (rule && rule.reward_variants) || [];
    for (const v of variants) {
      if (v.available) return v.id;
    }
    return null;
  };

  /**
   * Read the customer's stored pick for this rule. Returns the id
   * only if it still exists on the product AND is currently
   * available; otherwise returns null so the caller can fall back
   * to the first-available variant. We deliberately do NOT clear
   * the stored pick when it's OOS -- if the variant comes back in
   * stock later, the pick should still apply.
   */
  const getPickedVariantId = (rule) => {
    if (!rule || !rule.reward_has_multi_variants) return null;
    const variants = rule.reward_variants || [];
    if (variants.length === 0) return null;
    try {
      const raw = window.localStorage.getItem(VARIANT_PICK_STORAGE_PREFIX + rule.id);
      if (!raw) return null;
      const picked = parseInt(raw, 10);
      if (!picked) return null;
      for (const v of variants) {
        if (v.id === picked) {
          return v.available ? picked : null;
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  /**
   * Persist a variant pick for this rule. Passing null clears the
   * stored pick. Returns true if the stored value actually changed.
   */
  const setPickedVariantId = (rule, variantId) => {
    if (!rule || !rule.id) return false;
    const key = VARIANT_PICK_STORAGE_PREFIX + rule.id;
    try {
      const prev = window.localStorage.getItem(key);
      if (variantId) {
        const next = String(variantId);
        if (prev === next) return false;
        window.localStorage.setItem(key, next);
      } else {
        if (!prev) return false;
        window.localStorage.removeItem(key);
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  /**
   * Decide which variant id the reconciler should currently use
   * when adding / swapping a reward line for this rule.
   *
   * Order of preference:
   *   1. Single-variant reward   -> rule.reward_variant_id
   *   2. Multi-variant + stored  -> the stored pick (if available)
   *   3. Multi-variant fallback  -> first available variant
   *   4. None available          -> null (reconciler skips the add)
   */
  const resolveRewardVariantId = (rule) => {
    if (!rule) return null;
    if (!rule.reward_has_multi_variants) return rule.reward_variant_id || null;
    const picked = getPickedVariantId(rule);
    if (picked) return picked;
    return getFirstAvailableVariantId(rule);
  };

  const addReward = (rule, quantity) => {
    // Resolve the actual variant to add. For multi-variant rewards,
    // this honors the customer's saved pick and otherwise falls back
    // to the first available variant on the product.
    const variantId = resolveRewardVariantId(rule);
    if (!variantId || quantity <= 0) return Promise.resolve(null);
    // Defensive clamp: never request more than the per-line cap.
    // The reconciler normally keeps delta <= 1 for a fresh line, but
    // clamping here protects against any caller bug.
    const safeQty = Math.min(quantity, MAX_REWARD_LINE_QTY);
    const body = {
      items: [{
        id: variantId,
        quantity: safeQty,
        properties: {
          // Underscore prefix keeps these hidden from the customer in the
          // cart UI and in the checkout summary, but they are still
          // accessible to Shopify Functions (for discounting) and to
          // storefront CSS/JS (for styling the gift line in the cart).
          _bxgy_auto: 'true',
          _bxgy_rule_id: String(rule.id),
          _bxgy_source: 'theme-auto-add',
        },
      }],
    };
    return internalFetch(`${getRoot()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then((res) => {
      if (!res.ok) {
        return res.json().catch(() => ({})).then((err) => {
          console.warn('[bxgy] add failed', err);
          return null;
        });
      }
      return res.json();
    });
  };

  /**
   * Change a single line by 1-indexed position. Line numbers shift when
   * a line is removed, so callers that reduce multiple lines should
   * re-read the cart between changes (see `reconcileRule`).
   */
  const changeLineQuantity = (line, quantity) => internalFetch(`${getRoot()}cart/change.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ line, quantity }),
  }).then((res) => {
    if (!res.ok) {
      return res.json().catch(() => ({})).then((err) => {
        console.warn('[bxgy] change failed', err);
        return null;
      });
    }
    return res.json();
  });

  // --- per-rule reconciliation ----------------------------------------

  /**
   * Reconcile a single rule.
   * Options:
   *   earnedOverride (number) -- pre-computed earned count (bypasses
   *     the rule's own calculation). Used by reconcileAll after tier
   *     exclusion for 'once'-mode rules so a lower tier that would
   *     individually qualify still gets removed when a higher tier is
   *     active.
   *   forceRemove (bool) -- if true, ignore `remove_when_ineligible`
   *     and always reduce down to earnedOverride. Used when the rule
   *     is suppressed by tier exclusion.
   */
  const reconcileRule = (rule, options = {}) => {
    if (!rule || !rule.enabled) return Promise.resolve({ changed: false });
    if (!rule.reward_variant_id) {
      log('rule has no reward variant', rule.id, '- skipping');
      return Promise.resolve({ changed: false });
    }

    return getCart().then((cart) => {
      const currency = cart && cart.currency ? cart.currency : 'CAD';
      const threshold = getThresholdCents(rule, currency);
      const eligibleSubtotal = getEligibleSubtotal(cart, rule);
      const naturalEarned = getEarnedQuantity(rule, eligibleSubtotal, currency);
      const earned = (typeof options.earnedOverride === 'number')
        ? options.earnedOverride
        : naturalEarned;
      const current = getCurrentAutoAddedQuantity(cart, rule.id);

      // Multi-variant swap detection. If the currently-added gift
      // line points at a different variant than the one we now
      // want (either because the customer picked a new one via
      // the cart's "Change" CTA, or because the old pick went OOS
      // and resolveRewardVariantId fell back to a different one),
      // we need to remove the old line and add a fresh one under
      // the right variant.
      const resolvedVariantId = resolveRewardVariantId(rule);
      const currentLines = getCurrentAutoAddedLines(cart, rule.id);
      let swapNeeded = false;
      if (resolvedVariantId && currentLines.length > 0) {
        swapNeeded = currentLines.some((l) => l.item.variant_id !== resolvedVariantId);
      }

      info('reconcileRule', rule.id, {
        title: rule.title,
        cart_currency: currency,
        threshold_cents_used: threshold,
        min_spend_cad_cents: rule.min_spend_cad_cents,
        min_spend_usd_cents: rule.min_spend_usd_cents,
        eligibleSubtotalCents: eligibleSubtotal,
        naturalEarned,
        earned,
        current,
        repeat_mode: rule.repeat_mode,
        tier_suppressed: earned < naturalEarned,
        force_remove: !!options.forceRemove,
        reward_has_multi_variants: !!rule.reward_has_multi_variants,
        resolved_variant_id: resolvedVariantId,
        swap_needed: swapNeeded,
      });

      // Variant swap path: always wins over the earned=current
      // shortcut below, because "same qty, different variant"
      // still requires a cart mutation.
      if (swapNeeded && earned > 0) {
        info('  -> variant swap: clearing stale lines then re-adding with', resolvedVariantId);
        return reduceAutoAddedLines(rule.id, current)
          .then(() => addReward(rule, earned))
          .then(() => ({ changed: true }));
      }

      if (earned === current) {
        info('  -> no change needed (earned === current)');
        return { changed: false };
      }

      if (earned > current) {
        const delta = earned - current;
        info('  -> adding', delta, 'reward unit(s) for rule', rule.id);
        return addReward(rule, delta).then((result) => {
          info('  -> add result:', result ? 'ok' : 'failed');
          return { changed: true };
        });
      }

      // earned < current: need to reduce.
      // `remove_when_ineligible=false` is respected only when NOT being
      // force-removed by tier exclusion. Tier exclusion always wins,
      // because leaving a lower-tier reward in the cart alongside a
      // higher-tier one would violate the "tiered" mental model.
      if (earned === 0 && !rule.remove_when_ineligible && !options.forceRemove) {
        info('  -> earned=0 but remove_when_ineligible=false; keeping existing lines');
        return { changed: false };
      }

      const toReduce = current - earned;
      info('  -> reducing', toReduce, 'auto-added unit(s) for rule', rule.id,
        options.forceRemove ? '(forced by tier exclusion)' : '');
      return reduceAutoAddedLines(rule.id, toReduce).then(() => ({ changed: true }));
    });
  };

  /**
   * When BXGY is turned off (global checkbox) or every rule is disabled, no
   * reconcile pass runs — but customers may still have `_bxgy_auto` gift
   * lines from before (cached cart). Remove every line that this theme's
   * auto-add reconciler owns (`theme-auto-add` or legacy untagged). Does not
   * touch `_bxgy_source: bundle-set` (Custom Bundle Set) or other apps.
   * @returns {Promise<boolean>} whether any line was removed
   */
  const purgeThemeAutoAddGiftLines = () => getCart().then((cart) => {
    if (!cart || !Array.isArray(cart.items)) return false;
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (!isAutoAddedLine(item)) continue;
      const line = i + 1;
      info('purge inactive BXGY: removing theme auto-add line', line,
        `"${item.product_title}"`);
      return changeLineQuantity(line, 0)
        .then(() => purgeThemeAutoAddGiftLines())
        .then(() => true);
    }
    return false;
  });

  /**
   * Remove every auto-added line whose `_bxgy_rule_id` does NOT match
   * a currently enabled rule. Handles three real-world scenarios:
   *   - the merchant deleted or disabled a rule block after a customer
   *     had already received that reward
   *   - the merchant re-created a block (block IDs change, so the
   *     previously-added reward becomes an orphan)
   *   - the rule id stored on the reward has drifted due to a theme
   *     migration or section rename
   * Re-reads the cart between removals because /cart/change.js
   * reshuffles line numbers after each removal.
   */
  const removeOrphanAutoAddedLines = (enabledRuleIdSet) => getCart().then((cart) => {
    if (!cart || !Array.isArray(cart.items)) return null;
    let orphan = null;
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (!isAutoAddedLine(item)) continue;
      const rid = item.properties && item.properties._bxgy_rule_id;
      const ridStr = rid == null ? '' : String(rid);
      if (!enabledRuleIdSet[ridStr]) {
        orphan = {
          line: i + 1,
          quantity: item.quantity,
          rid: ridStr,
          title: item.product_title,
        };
        break;
      }
    }
    if (!orphan) return null;
    info('orphan cleanup: removing line', orphan.line,
      `"${orphan.title}"`,
      `qty=${orphan.quantity}`,
      `_bxgy_rule_id="${orphan.rid}" (no matching enabled rule)`);
    return changeLineQuantity(orphan.line, 0)
      .then(() => removeOrphanAutoAddedLines(enabledRuleIdSet));
  });

  /**
   * Anti-tampering step: walk the cart and cap every auto-added line
   * to `MAX_REWARD_LINE_QTY`. This catches the case where a customer
   * (or a malicious script) issued /cart/change.js directly to bump
   * a free-gift line up to 5, 10, etc. Since our fetch monkey-patch
   * triggers `scheduleReconcile` on every cart mutation, this runs
   * moments after any tamper attempt.
   *
   * Returns the number of lines that had to be clamped, so the caller
   * can flag `anyChanged` for the UI refresh event.
   */
  const clampAutoAddedLineQuantities = () => getCart().then((cart) => {
    if (!cart || !Array.isArray(cart.items)) return 0;
    // Find the FIRST over-quantity auto-added line, clamp it, then
    // recurse. We only mutate one line per pass because
    // /cart/change.js reshuffles line numbers on removal (though
    // here we only reduce, not remove, so line numbers should be
    // stable -- still, recursing keeps this aligned with the rest
    // of the reconciler and makes the logic resilient if Shopify
    // ever changes that behaviour).
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (!isAutoAddedLine(item)) continue;
      if (item.quantity <= MAX_REWARD_LINE_QTY) continue;
      info('qty clamp: line', i + 1,
        `"${item.product_title}"`,
        `qty=${item.quantity}`,
        '-> forcing to', MAX_REWARD_LINE_QTY,
        '(anti-tamper)');
      return changeLineQuantity(i + 1, MAX_REWARD_LINE_QTY)
        .then(() => clampAutoAddedLineQuantities())
        .then((rest) => rest + 1);
    }
    return 0;
  });

  /**
   * Reduce `toReduce` total units across the auto-added lines for
   * `ruleId`. Re-reads the cart between changes because /cart/change.js
   * reshuffles line numbers whenever a line is removed.
   */
  const reduceAutoAddedLines = (ruleId, toReduce) => {
    if (toReduce <= 0) return Promise.resolve();
    return getCart().then((cart) => {
      const lines = getCurrentAutoAddedLines(cart, ruleId);
      if (lines.length === 0 || toReduce <= 0) return null;
      // Reduce from the last line first so earlier line numbers stay stable
      // for the current change, minimizing surprises if anything fails mid-way.
      const target = lines[lines.length - 1];
      const reduceBy = Math.min(toReduce, target.quantity);
      const newQty = target.quantity - reduceBy;
      return changeLineQuantity(target.line, newQty)
        .then(() => reduceAutoAddedLines(ruleId, toReduce - reduceBy));
    });
  };

  // --- cart update broadcast ------------------------------------------
  //
  // Anything that wants to react to BXGY-driven cart changes (the
  // Vue motivation bar, future subscribers) listens for the
  // `bxgy:cart-updated` event and reads the cart off `event.detail`.
  // This is the single outbound event the reconciler emits when
  // its work is done -- callers should never need to re-fetch
  // /cart.js themselves unless they missed the event.

  const dispatchCartUpdated = (cart) => {
    if (!cart) return;
    try {
      document.dispatchEvent(new CustomEvent('bxgy:cart-updated', {
        detail: { cart },
      }));
    } catch (e) { /* noop */ }
  };

  // Small HTML escapers so user-supplied strings (rule titles,
  // product names) cannot break the innerHTML we inject above.
  const escapeHtml = (str) => String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  const escapeAttr = (str) => escapeHtml(str);

  // --- top-level reconcile --------------------------------------------

  const isBxgyPromoActive = (config) =>
    !!(config && config.enabled_global
      && (config.rules || []).some((r) => r && r.enabled));

  const reconcileAll = () => {
    if (isReconciling) {
      info('reconcileAll: already reconciling, queueing');
      reconcileQueued = true;
      return Promise.resolve();
    }

    const config = getRuleConfig();

    if (!isBxgyPromoActive(config)) {
      info('reconcileAll: BXGY inactive (master off and/or no enabled rules) — purging theme auto-add gifts if any');
      isReconciling = true;
      setReconcilingFlag(true);
      return purgeThemeAutoAddGiftLines().then((didRemove) => {
        isReconciling = false;
        if (didRemove) {
          getCart().then((finalCart) => {
            dispatchCartUpdated(finalCart);
            try {
              document.dispatchEvent(new CustomEvent('bxgy:reconciled'));
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            } catch (e) { /* noop */ }
          }).catch(() => { /* noop */ });
        }
        if (reconcileQueued) {
          reconcileQueued = false;
          setTimeout(reconcileAll, 0);
        } else {
          setReconcilingFlag(false);
        }
      }).catch((err) => {
        isReconciling = false;
        setReconcilingFlag(false);
        console.warn('[BXGY] purge inactive reconciler error', err);
      });
    }

    const rules = (config.rules || []).filter((r) => r && r.enabled);
    info('reconcileAll: starting. enabled rules =', rules.length, 'of', (config.rules || []).length, 'total');

    isReconciling = true;
    // Ensure the UI loading state is on for the full reconcile, even
    // when reconcileAll is invoked directly (not through the
    // scheduleReconcile debounce path).
    setReconcilingFlag(true);
    let anyChanged = false;

    // Build the set of currently-enabled rule IDs so we can recognise
    // orphan auto-added lines (reward items whose _bxgy_rule_id no
    // longer maps to any enabled rule block).
    const enabledRuleIdSet = {};
    rules.forEach((r) => { enabledRuleIdSet[String(r.id)] = true; });

    // Step 1: clean up orphan auto-added lines first. If we skip this,
    // a stale reward (e.g. from a deleted/renamed block) would persist
    // forever because no current rule "owns" it. This is typically what
    // causes "I still see two free items" after editing rule blocks.
    return removeOrphanAutoAddedLines(enabledRuleIdSet).then((cleanupResult) => {
      if (cleanupResult !== null) anyChanged = true;
      // Step 2: anti-tamper pass. Clamp any auto-added line whose
      // quantity exceeds MAX_REWARD_LINE_QTY back down to the cap.
      // This protects against a customer using DevTools or a browser
      // extension to bump a free-gift line quantity.
      return clampAutoAddedLineQuantities();
    }).then((clampedCount) => {
      if (clampedCount > 0) anyChanged = true;
      // Step 3: snapshot cart (fresh, post-cleanup + post-clamp) so
      // evaluations are consistent before mutating anything further.
      return getCart();
    }).then((cart) => {
      const currency = cart && cart.currency ? cart.currency : 'CAD';

      // Broadcast the pre-mutation cart so the Vue motivation bar
      // (assets/bxgy-motivation-bar.js) can paint progress
      // immediately. We emit again at the end of the reconcile
      // below once any reward add/remove has settled.
      dispatchCartUpdated(cart);

      // Step 4: compute baseline evaluation for each rule.
      const evals = rules.map((rule) => {
        const subtotal = getEligibleSubtotal(cart, rule);
        const naturalEarned = getEarnedQuantity(rule, subtotal, currency);
        return {
          rule,
          subtotal,
          naturalEarned,
          earned: naturalEarned,
          threshold: getThresholdCents(rule, currency),
          forceRemove: false,
        };
      });

      // Step 5: apply tier exclusion across 'once'-mode rules.
      // Only the highest-threshold rule that currently qualifies fires;
      // the rest forfeit to 0 and are force-removed so a stale lower-
      // tier reward does not linger when the customer crosses into a
      // higher tier.
      const onceQualifying = evals.filter((e) => e.rule.repeat_mode === 'once' && e.naturalEarned > 0);
      if (onceQualifying.length > 0) {
        onceQualifying.sort((a, b) => b.threshold - a.threshold);
        const winnerId = onceQualifying[0].rule.id;
        info('tier exclusion: once-mode winner =', winnerId,
          'threshold =', onceQualifying[0].threshold,
          'candidates =', onceQualifying.length);
        evals.forEach((e) => {
          if (e.rule.repeat_mode === 'once' && e.rule.id !== winnerId) {
            if (e.naturalEarned > 0) {
              info('  -> suppressing once-mode rule', e.rule.id,
                `(threshold ${e.threshold}, would have earned ${e.naturalEarned})`);
            }
            e.earned = 0;
            e.forceRemove = true;
          }
        });
      } else {
        info('tier exclusion: no once-mode rules currently qualify');
      }

      // Step 6: reconcile each rule sequentially against its final
      // earned count. Sequential (not parallel) because /cart/change.js
      // shifts line numbers and parallel writes can race.
      let chain = Promise.resolve();
      evals.forEach((e) => {
        chain = chain.then(() => reconcileRule(e.rule, {
          earnedOverride: e.earned,
          forceRemove: e.forceRemove,
        }).then((res) => {
          if (res && res.changed) anyChanged = true;
        }).catch((err) => {
          console.warn('[BXGY] rule failed', e.rule && e.rule.id, err);
        }));
      });
      return chain;
    }).then(() => {
      isReconciling = false;
      // Re-fetch the final cart and broadcast it so the Vue
      // motivation bar (and any other subscriber) sees the
      // post-reconcile state without having to re-fetch itself.
      getCart().then((finalCart) => {
        dispatchCartUpdated(finalCart);
      }).catch(() => { /* noop */ });
      // Populate any newly-rendered variant-picker mounts on the
      // freshly-swapped cart HTML.
      try { refreshVariantPickers(); } catch (e) { /* noop */ }
      if (anyChanged) {
        // Let the theme (e.g. cart drawer) know it should re-render.
        try {
          document.dispatchEvent(new CustomEvent('bxgy:reconciled'));
          // Best-effort: fire the most commonly used theme events.
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch (e) { /* noop */ }
      }
      if (reconcileQueued) {
        // Another mutation arrived while we were reconciling; keep
        // the spinner visible until the queued pass also completes.
        reconcileQueued = false;
        setTimeout(reconcileAll, 0);
      } else {
        setReconcilingFlag(false);
      }
    }, (err) => {
      isReconciling = false;
      setReconcilingFlag(false);
      console.warn('[bxgy] reconcileAll error', err);
    });
  };

  const scheduleReconcile = (delay = 250) => {
    const ms = typeof delay === 'number' ? delay : 250;
    info(`scheduleReconcile(${ms}ms)`);
    // Flip the loading flag immediately so the spinner shows during
    // the debounce window too (otherwise the customer sees their
    // own cart update complete, then the gift line silently change
    // 250ms later with no visual cue).
    setReconcilingFlag(true);
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      info('debounce fired -> reconcileAll');
      reconcileAll();
    }, ms);
  };

  // --- external cart mutation detection -------------------------------

  /**
   * Wrap window.fetch so that any external code that mutates the cart
   * (add / change / update / clear) triggers a reconcile. Our own
   * mutations are skipped via the `internalCallInFlight` counter so we
   * don't loop on ourselves.
   */
  const patchFetch = () => {
    if (!window.fetch) return;
    if (window.fetch.__bxgyPatched) return;

    const origFetch = window.fetch.bind(window);
    const patched = (input, init) => {
      let url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { /* noop */ }

      const isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i.test(url);
      const wasInternal = internalCallInFlight > 0;

      const result = origFetch(input, init);

      if (isCartMutation) {
        info('fetch patch: detected cart mutation ->', url, wasInternal ? '(internal, ignored)' : '(external, will reconcile)');
        if (!wasInternal) {
          result.then(() => {
            scheduleReconcile(300);
          }).catch(() => { /* ignore */ });
        }
      }
      return result;
    };
    patched.__bxgyPatched = true;
    window.fetch = patched;
    info('fetch patched successfully');
  };

  /**
   * Many themes (including cart drawers) emit CustomEvents after cart
   * operations. Listen to a handful of common names and let the
   * debouncer coalesce any storm of events into a single reconcile.
   * Also intercept jQuery's ajaxSuccess if jQuery happens to be in use
   * (older themes), so jQuery-based cart updates are caught too.
   */
  const bindThemeEvents = () => {
    const events = [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'cart:added',
      'cart:build',
      'ajaxCart.afterCartLoad',
      'cart-drawer:open',
      'cart-drawer:refresh',
      'cart-drawer:updated',
      'shopify:cart:update',
      'ajaxProduct:added',
    ];
    for (const evName of events) {
      document.addEventListener(evName, () => {
        info('heard theme event:', evName, '-> scheduling reconcile');
        scheduleReconcile(300);
      });
    }

    // jQuery-based themes (rare in modern Dawn-derived themes but still
    // around in older OS2 installs).
    if (window.jQuery) {
      try {
        window.jQuery(document).on('ajaxSuccess', (_evt, _xhr, settings) => {
          const url = (settings && settings.url) || '';
          if (/\/cart\/(add|change|update|clear)(\.js)?/i.test(url)
              && internalCallInFlight === 0) {
            scheduleReconcile(300);
          }
        });
      } catch (e) { /* noop */ }
    }
  };

  // --- init ------------------------------------------------------------

  // --- inline variant picker (on gift cart lines) ---------------------
  //
  // The Liquid snippet bxgy-variant-picker.liquid emits an invisible
  // `<div data-bxgy-picker-mount>` inside each gift cart item. The
  // mount carries rule + variant ids as data attributes, but never
  // renders UI itself. Instead, the populator below finds the cart
  // item's existing option row (e.g. "Size: Small") and injects:
  //
  //   - a "Change" CTA beside the option value, and
  //   - a hidden <select> that takes the value's place on click.
  //
  // This keeps the picker visually grouped with the option it
  // modifies, which matches what customers expect from Shopify
  // cart-line variant swaps.

  /**
   * Find the `.cart-item__option-value` row that corresponds to the
   * reward's configurable option name (e.g. "Size"). Matching is
   * done against the adjacent `.cart-item__option-name` span's text
   * ("Size:"), case-insensitively. Returns { row, nameEl, valueEl }
   * or null if no match is found -- e.g. the theme doesn't render
   * options for this line, or the option is named something else
   * in the product.
   */
  const findOptionRow = (cartItemEl, optionName) => {
    if (!cartItemEl || !optionName) return null;
    const target = String(optionName).trim().toLowerCase();
    const nameEls = cartItemEl.querySelectorAll('.cart-item__option-name');
    for (const nameEl of nameEls) {
      let raw = (nameEl.textContent || '').trim().toLowerCase();
      // Strip trailing colon from the theme's "Size:" rendering.
      if (raw.charAt(raw.length - 1) === ':') raw = raw.slice(0, -1).trim();
      if (raw === target) {
        const row = nameEl.parentElement;
        const valueEl = row && row.querySelector('.cart-item__option-value');
        if (row && valueEl) return { row, nameEl, valueEl };
      }
    }
    return null;
  };

  const populateVariantPickerMount = (mountEl) => {
    if (!mountEl || mountEl.getAttribute('data-bxgy-picker-populated') === '1') return;
    const ruleId = mountEl.getAttribute('data-rule-id');
    if (!ruleId) return;

    const config = getRuleConfig();
    const rule = (config.rules || []).find((r) => r && String(r.id) === String(ruleId));
    // If the rule is missing, disabled, or single-variant, there is
    // nothing to swap between. Mark the mount populated so we don't
    // keep re-checking it on every reconcile tick.
    if (!rule || !rule.reward_has_multi_variants || (rule.reward_variants || []).length < 2) {
      mountEl.setAttribute('data-bxgy-picker-populated', '1');
      return;
    }

    const optionName = (rule.reward_option_names && rule.reward_option_names[0]) || 'option';
    const currentVariantId = parseInt(mountEl.getAttribute('data-variant-id'), 10) || null;

    // Find the option row to attach to. If the cart item's option
    // rows haven't rendered yet (theme may render them in a later
    // tick), we bail without marking populated so a subsequent
    // MutationObserver tick can try again.
    const cartItemEl = mountEl.closest('.cart-item');
    const target = findOptionRow(cartItemEl, optionName);
    if (!target) {
      log('variant picker: no matching option row yet for rule', rule.id, `(${optionName})`);
      return;
    }

    // Avoid double-inject if this row was already decorated (can
    // happen if the mount is repopulated after a partial DOM swap).
    if (target.row.querySelector('[data-bxgy-picker-toggle]')) {
      mountEl.setAttribute('data-bxgy-picker-populated', '1');
      return;
    }

    const opts = rule.reward_variants.map((v) => {
      const isCurrent = v.id === currentVariantId;
      const disabled = (!v.available && !isCurrent) ? ' disabled' : '';
      const selected = isCurrent ? ' selected' : '';
      const titleFirst = String(v.title || '').split('/')[0].trim();
      const label = titleFirst + (!v.available && !isCurrent ? ' \u2014 sold out' : '');
      return `<option value="${escapeAttr(String(v.id))}"${disabled}${selected}>${escapeHtml(label)}</option>`;
    });

    // Build and inject the "Change" button + the picker body into
    // the option row. We mark each piece with the owning rule id so
    // the delegated listeners can route events back to the right
    // mount without needing a wrapping element.
    const toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.className = 'bxgy-variant-picker__toggle btn btn--underline btn--small';
    toggleBtn.setAttribute('data-bxgy-picker-toggle', '');
    toggleBtn.setAttribute('data-bxgy-rule-id', String(rule.id));
    toggleBtn.textContent = 'change';

    const body = document.createElement('span');
    body.className = 'bxgy-variant-picker__body';
    body.setAttribute('data-bxgy-picker-body', '');
    body.setAttribute('data-bxgy-rule-id', String(rule.id));
    body.hidden = true;
    body.innerHTML = (
      `<select class="bxgy-variant-picker__select" data-bxgy-picker-select>${opts.join('')}</select>`
      + '<button type="button" class="bxgy-variant-picker__cancel" data-bxgy-picker-cancel aria-label="Cancel">\u00d7</button>'
    );

    // Order in the row:  [Name:] [Value] [(change)] [Select + X]
    target.row.appendChild(toggleBtn);
    target.row.appendChild(body);

    mountEl.setAttribute('data-bxgy-picker-populated', '1');
  };

  const refreshVariantPickers = () => {
    const mounts = document.querySelectorAll('[data-bxgy-picker-mount]');
    mounts.forEach(populateVariantPickerMount);
  };

  /**
   * Delegated click/change handler for every variant picker in the
   * document. Delegation is important because the cart drawer blows
   * away and re-renders its inner HTML on updates, so any listeners
   * bound directly to a select would be lost after the first cart
   * change.
   */
  /**
   * Expand (or collapse) the picker inside a given option row. We
   * swap visibility of:
   *   - `.cart-item__option-value`  (the static "Small" text)
   *   - `[data-bxgy-picker-toggle]` (the "(change)" link)
   *   - `[data-bxgy-picker-body]`   (the select + cancel X)
   */
  const setPickerOpen = (row, open) => {
    if (!row) return;
    const valueEl = row.querySelector('.cart-item__option-value');
    const toggle = row.querySelector('[data-bxgy-picker-toggle]');
    const body = row.querySelector('[data-bxgy-picker-body]');
    if (open) {
      if (valueEl) valueEl.setAttribute('hidden', '');
      if (toggle) toggle.setAttribute('hidden', '');
      if (body) body.hidden = false;
    } else {
      if (valueEl) valueEl.removeAttribute('hidden');
      if (toggle) toggle.removeAttribute('hidden');
      if (body) body.hidden = true;
    }
  };

  const bindVariantPickerDelegation = () => {
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest && e.target.closest('[data-bxgy-picker-toggle]');
      if (toggle) {
        e.preventDefault();
        setPickerOpen(toggle.parentElement, true);
        return;
      }
      const cancel = e.target.closest && e.target.closest('[data-bxgy-picker-cancel]');
      if (cancel) {
        e.preventDefault();
        // Walk up to the option row (cancel -> body -> row).
        const body = cancel.closest('[data-bxgy-picker-body]');
        setPickerOpen(body && body.parentElement, false);
      }
    });

    document.addEventListener('change', (e) => {
      const sel = e.target.closest && e.target.closest('[data-bxgy-picker-select]');
      if (!sel) return;
      const body = sel.closest('[data-bxgy-picker-body]');
      const ruleId = body && body.getAttribute('data-bxgy-rule-id');
      if (!ruleId) return;
      const config = getRuleConfig();
      const rule = (config.rules || []).find((r) => r && String(r.id) === String(ruleId));
      if (!rule) return;
      const newId = parseInt(sel.value, 10) || null;
      setPickedVariantId(rule, newId);
      // Swap will happen on the next reconcile (reconcileRule
      // detects variant mismatch and removes+re-adds the line).
      scheduleReconcile(0);
    });
  };

  const initBxgyAutoAdd = () => {
    info('init: DOM ready state =', document.readyState);
    const config = getRuleConfig();

    patchFetch();
    bindThemeEvents();

    if (!isBxgyPromoActive(config)) {
      info('init: BXGY promo inactive — cart listeners active; stale theme gifts will be removed on load and on cart changes.');
      info('       data-bxgy-rules tags:', document.querySelectorAll('script[data-bxgy-rules]').length);
      scheduleReconcile(150);
      return;
    }

    bindVariantPickerDelegation();

    // Seed the motivation bar with the current cart before the
    // first reconcile lands (the Vue app also does its own
    // /cart.js fetch, but dispatching here means data flow stays
    // consistent across every code path).
    getCart().then((cart) => {
      dispatchCartUpdated(cart);
    }).catch(() => { /* noop */ });

    // Populate any variant pickers that exist on initial page load
    // (e.g. cart page has gift lines rendered from Liquid).
    try { refreshVariantPickers(); } catch (e) {
      console.warn('[bxgy] initial variant picker populate failed', e);
    }

    // Re-populate any variant-picker mounts that appear when the
    // theme swaps the cart subtree for fresh HTML (cart drawer
    // updates, cart page section refetch, etc.). New mounts
    // start without the `data-bxgy-picker-populated` attr, so
    // refreshVariantPickers is cheap to call unconditionally.
    // (The motivation bar has its own MutationObserver in
    // bxgy-motivation-bar.js.)
    try {
      const obs = new MutationObserver(() => refreshVariantPickers());
      obs.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
      console.warn('[bxgy] could not attach variant-picker MutationObserver', e);
    }

    // Initial pass shortly after load so we don't race the theme's own
    // cart initialization.
    info('init: scheduling initial reconcile pass');
    scheduleReconcile(150);
  };

  if (document.readyState === 'loading') {
    info('waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', initBxgyAutoAdd);
  } else {
    info('DOM already parsed, initing now');
    initBxgyAutoAdd();
  }

  // --- public API ------------------------------------------------------

  window.BxgyAutoAdd = {
    reconcile: () => scheduleReconcile(0),
    reconcileNow: () => reconcileAll(),
    getConfig: () => getRuleConfig(),
    _setDebug: (v) => { DEBUG = !!v; },
  };
})();
